import type { IResponseBase } from '@/interfaces';
import type { IGuaranteeOfferSelectResponse, IGuaranteeOffers } from '@/interfaces/IGuaranteeOffers';
import type {
	ISuretyInsuranceQuotation,
	ISuretyInsuranceSummary,
} from '@/interfaces/ISuretyInsuranceQuotation';
import { Http } from './http';

const http = Http.create();

/**
 * Self-service do Seguro Fiança multicálculo. A criação do caso usa o endpoint genérico
 * (CaseService.addCase com caseType = CASE_SURETY_INSURANCE), que dispara o fan-out das
 * seguradoras. Aqui ficam o comparativo, a seleção, a recotação e o reenvio da biometria.
 */
export class SuretyInsuranceService {
	/** Vitrine do marketplace da Garantia: uma oferta por garantidora, no formato único (D35). */
	static async getGuaranteeOffers(guaranteeCaseId: string): Promise<IGuaranteeOffers> {
		const response = await http.get<IResponseBase<IGuaranteeOffers>>(
			`/cases/${guaranteeCaseId}/guarantee-offers`,
		);
		return response.data.data;
	}

	/**
	 * Escolha única da vitrine. A resposta diz o próximo passo: PRODUCT_STEP (abrir a etapa de
	 * produto da Carta Fiança com o produto marcado) ou CONTRACTING (a seguradora foi selecionada).
	 */
	static async selectGuaranteeOffer(
		guaranteeCaseId: string,
		offerId: string,
		planId: string,
	): Promise<IGuaranteeOfferSelectResponse> {
		const response = await http.post<IResponseBase<IGuaranteeOfferSelectResponse>>(
			`/cases/${guaranteeCaseId}/guarantee-offers/select`,
			{ offerId, planId },
		);
		return response.data.data;
	}

	/** Comparativo das cotações (seguradora × plano) do caso. */
	static async getComparison(caseId: string): Promise<ISuretyInsuranceQuotation[]> {
		const response = await http.get<IResponseBase<ISuretyInsuranceQuotation[]>>(
			`/surety-insurance/self-service/${caseId}/quotations`,
		);
		return response.data.data;
	}

	/** Cotação escolhida + proposta/apólice do caso (aba Informações Gerais). */
	static async getSummary(caseId: string): Promise<ISuretyInsuranceSummary> {
		const response = await http.get<IResponseBase<ISuretyInsuranceSummary>>(
			`/surety-insurance/self-service/${caseId}/summary`,
		);
		return response.data.data;
	}

	/** Seleciona a cotação escolhida e dispara os pré-requisitos da seguradora (biometria). */
	static async selectQuotation(caseId: string, quotationId: string): Promise<void> {
		await http.post(`/surety-insurance/self-service/${caseId}/quotations/${quotationId}/select`);
	}

	/** Redispara o fan-out inteiro (vigência/valores mudaram, cotação expirou, falha geral). */
	/** "Recotar" da vitrine da Garantia (D47 F2.3): o backend roteia (caso-filho ou solicitação). */
	static async requoteGuaranteeOffers(guaranteeCaseId: string): Promise<void> {
		await http.post(`/cases/${guaranteeCaseId}/guarantee-offers/requote`);
	}

	static async requoteAll(caseId: string): Promise<void> {
		await http.post(`/surety-insurance/self-service/${caseId}/requote`);
	}

	/**
	 * Biometria recusada: nova tentativa no mesmo plano. A seguradora encerra a cotação recusada,
	 * então o backend recota, reseleciona o plano e envia um novo link de biometria por WhatsApp.
	 */
	static async retryBiometry(caseId: string): Promise<void> {
		await http.post(`/surety-insurance/self-service/${caseId}/biometry/retry`);
	}

	/**
	 * Locador da Garantia cadastrada sem ele, pedido depois da biometria aprovada. O backend inclui
	 * o locador na cotação da seguradora e segue para a emissão da proposta.
	 */
	static async setLessor(caseId: string, lessor: { document: string; name: string }): Promise<void> {
		await http.put(`/surety-insurance/self-service/${caseId}/lessor`, lessor);
	}

	/** Reenvia ao locatário o WhatsApp com o link de biometria vigente (link se perde). */
	static async resendBiometry(caseId: string): Promise<void> {
		await http.post(`/surety-insurance/self-service/${caseId}/biometry/resend`);
	}

	/**
	 * Link público de biometria do caso (redireciona para a URL viva da seguradora).
	 * É o mesmo destino do botão do WhatsApp — serve para copiar/compartilhar pela tela.
	 */
	static biometryPublicUrl(caseId: string): string {
		return `${http.defaults.baseURL?.replace(/\/$/, '')}/public/surety-insurance/${caseId}/biometry`;
	}

	/**
	 * Link público do PDF do boleto à vista anexado ao caso — mesmo destino do botão do WhatsApp.
	 * 404 enquanto a seguradora não gerou o boleto.
	 */
	static boletoPublicUrl(caseId: string): string {
		return `${http.defaults.baseURL?.replace(/\/$/, '')}/public/surety-insurance/${caseId}/boleto`;
	}
}
